import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";
import { isEmpty } from "lodash";
import { fetchAppMetadata } from '../../shared-components/utils/common'
import { EntryFieldExtensionContext } from "../contexts/entryFieldExtensionContext";
import { ChatGPTAppMetadataType } from "../../shared-components/common/types/AppMetadata";
import { AppConfigMissing } from "../../components/AppConfigMissing";
import LoadingScreen from "../../shared-components/LoadingScreen/LoadingScreen";
import UiLocation from "@contentstack/app-sdk/dist/src/uiLocation";
import { getLatestExperienceVersion, getAudienceList, getExperience, getLocaleList, isPersonalizeEnabledOnCurrentPlan, getAIAAPIEndpoint, getCustomLocaleList } from "../../shared-components/utils/helper";
import { IAudience, IExperienceType, IExperienceVersion, IVariant } from "../../shared-components/types";
import { useMarketPlaceApp } from "../hooks/useMarketPlaceApp";

export const EntryFieldExtensionProvider = ({ children }: any) => {
  const [token, setToken] = useState<string>('')
  const [configMissing, setConfigMissing] = useState<boolean>(false);
  const [entryField, setEntryField] = useState<unknown>(null);
  const [appMetadata, setAppMetadata] = useState<ChatGPTAppMetadataType | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [configLoading, setConfigLoading] = useState<boolean>(true)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [errorHeaderMessage, setErrorHeaderMessage] = useState<string>("")
  const [audienceList, setAudienceList] = useState<IAudience[]>([]);
  const [showPersonalize, setShowPersonalize] = useState(false);

  const experienceTypeRef = useRef<IExperienceType | null>(null);
  const currentVariantRef = useRef<IVariant | null>(null);
  const localeListRef = useRef<Record<string, string> | null>(null);
  const { appSdk, appConfig, appRegion, location } = useMarketPlaceApp();

  useEffect(() => {
    (async () => {
      // check if the data was loaded earlier or not
      if (isEmpty(entryField)) {
        setLoading(true);
        let fieldData = await location.field.getData();
        setEntryField(fieldData);
        setLoading(false);
      }
    })();
  }, [setLoading, setEntryField, location, entryField]);

  useEffect(() => {
    fetchAppMetadata().then((data) => {
      setAppMetadata(data)
    }).catch(() => {
      setErrorMessage('Failed to get the initial config.')
    })
  }, [])

  useEffect(() => {
    (async () => {
      if (!appSdk || !appConfig || !appRegion) return
      console.log("app region in entry", appRegion);
      const [localeList, entryLocaleList] = await Promise.all([
        getLocaleList(appRegion),
        getCustomLocaleList(appRegion, appSdk.stack.getData().api_key),
      ]);
      setPersonalizeStatus(appSdk)
      const mergedLocaleList: Record<string, string> = {
        ...localeList.locales,
        ...entryLocaleList,
      };
      localeListRef.current = mergedLocaleList;
      if (!appConfig.mode && appConfig.isBrandKitEnabled !== true) setConfigMissing(true);
      const url = new URL(window.location.href);
      const getTokenFromBackend = async () => {
        const response = await axios.post(`${getAIAAPIEndpoint(appRegion)}/v1/exchangeToken`, {
          token: url.searchParams.get("app-token"),
        });
        setToken(response.data.data.token as string);
        setConfigLoading(false)
      };
      getTokenFromBackend().catch(() => setErrorMessage('The servers are encountering a significant increase in traffic.'));
    })()
  }, [appSdk, appConfig, appRegion])

  const setFieldData = useCallback(
    async (data: unknown) => {
      setLoading(true);
      await location.field.setData(data);
      setEntryField(data);
      setLoading(false);
    },
    [location, setLoading, setEntryField]
  );

  const getToken = useCallback(
    () => token, [token]
  )

  const getAudienceUids = (exp: IExperienceVersion, variantShortUid: string) => {
      const currentVariant = exp.variants?.find((variant: IVariant) => variant.shortUid === variantShortUid);
      if(currentVariant) currentVariantRef.current = currentVariant;
      return currentVariant?.audiences || []
  }

  const getCurrentVariantId = () => {
    const data = location?.entry?.getData();
    return data?.variant_uid || data?._variant?._uid || data?._variant_uid || Object.values(data?._applied_variants || {})?.[0] || null;
  }

  const setPersonalizeStatus = async (appSDK: UiLocation) => {
    const isPersonalizeEnabled = await isPersonalizeEnabledOnCurrentPlan();
    const currentVariantId = getCurrentVariantId();
    if(!isPersonalizeEnabled || !currentVariantId || !appRegion) return;

    const variant = await appSDK?.stack.getVariantById(currentVariantId)
    const { experience_uid, project_uid, variant_short_uid } = variant?.personalize_metadata || {};
    const experience = await getExperience(appRegion, experience_uid, project_uid, appSDK?.stack.getData().api_key);
    experienceTypeRef.current = experience?.__type;
    setShowPersonalize(isPersonalizeEnabled && process.env.REACT_APP_SHOW_PERSONALIZE === 'true');
    const latestExp = await getLatestExperienceVersion(appRegion, experience_uid, project_uid, appSDK?.stack.getData().api_key);
    if (latestExp) {
      const audienceUids = getAudienceUids(latestExp, variant_short_uid);
      const audList = await getAudienceList(appRegion, audienceUids, project_uid);
      setAudienceList(audList)
    } else {
      console.warn('Active experience not found');
    }
  }

  if (configLoading)
    return <LoadingScreen showMessage={false} />;

  if (configMissing) {
    return <AppConfigMissing appInstallationUID={appSdk?.installationUID} appRegion={appRegion} />;
  }

  return (
    <EntryFieldExtensionContext.Provider
      value={{
        entryField,
        setFieldData,
        loading,
        appMetadata,
        getToken,
        errorMessage,
        setErrorMessage,
        errorHeaderMessage,
        setErrorHeaderMessage,
        showPersonalize,
        audienceList,
        experienceType: experienceTypeRef.current,
        currentVariant: currentVariantRef.current,
        localeList: localeListRef.current
      }}
    >
      {children}
    </EntryFieldExtensionContext.Provider>
  );
};